import { FC, ReactElement, useState } from 'react'
import { useClientConnectedUser } from '@stream-io/feeds-client/react-bindings'

import { InfoListItem, LoadingSpinner, Select } from '@/components'
import { EnableNotificationsBanner } from '@/compositions'
import { useChatConnection } from '@/context/ChatConnectionContext'
import { useFeedConnection } from '@/context/FeedConnectionContext'
import {
    deriveNotificationCategoryLevels,
    isNotificationLevel,
    saveNotificationCategoryLevels,
} from '@/helpers/notificationCategoryLevels'
import { CATEGORY_SUPPORTS_PUSH } from '@/helpers/pushPreferences'
import { trans } from '@/helpers/trans'
import { usePushRegistration } from '@/hooks/usePushRegistration'
import { FormOption } from '@/types/formTypes'
import {
    CATEGORY_ORDER,
    DEFAULT_NOTIFICATION_CATEGORY_LEVELS,
    NotificationCategory,
    NotificationCategoryLevels,
    NotificationLevel,
} from '@/types/notificationPreferenceTypes'

import './NotificationSettingsPage.scss'

const SWITCH_ATTRIBUTE: Record<string, string> = { switch: '' }

const GLOBAL_SWITCH_ID = 'notification-settings-global-switch'
const getCategorySelectId = (category: NotificationCategory): string => `notification-settings-category-${category}`

const getLevelOptions = (category: NotificationCategory): FormOption[] => {
    const options: FormOption[] = []

    if (CATEGORY_SUPPORTS_PUSH[category]) {
        options.push({
            value: NotificationLevel.pushAndInApp,
            label: trans('pages.notificationSettings.levels.pushAndInApp'),
        })
    }

    options.push({ value: NotificationLevel.inAppOnly, label: trans('pages.notificationSettings.levels.inAppOnly') })
    options.push({ value: NotificationLevel.off, label: trans('pages.notificationSettings.levels.off') })

    return options
}

export const NotificationSettingsPage: FC = (): ReactElement => {
    const { client: chatClient, userId } = useChatConnection()
    const { client: feedsClient } = useFeedConnection()
    const connectedFeedsUser = useClientConnectedUser()
    const {
        bridgeAvailable, status: permissionStatus, enabled: globalEnabled, loading: globalLoading, enable, disable,
    } = usePushRegistration()

    const [overrideLevels, setOverrideLevels] = useState<NotificationCategoryLevels | null>(null)

    const hydratedLevels = deriveNotificationCategoryLevels(chatClient, connectedFeedsUser, userId)
    const levels = overrideLevels ?? hydratedLevels ?? DEFAULT_NOTIFICATION_CATEGORY_LEVELS
    const hydrated = !! hydratedLevels || !! overrideLevels

    const saveLevels = async (nextLevels: NotificationCategoryLevels): Promise<void> => {
        setOverrideLevels(nextLevels)

        if (! chatClient || ! feedsClient) {
            return
        }

        await saveNotificationCategoryLevels(chatClient, feedsClient, userId, nextLevels)
    }

    const handleCategoryChange = (category: NotificationCategory, value: string): void => {
        if (! isNotificationLevel(value)) {
            return
        }

        saveLevels({ ...levels, [category]: value }).catch(error => console.error(error))
    }

    const handleCategoryCheckboxChange = (category: NotificationCategory, checked: boolean): void => {
        handleCategoryChange(category, checked ? NotificationLevel.pushAndInApp : NotificationLevel.off)
    }

    const handleGlobalChange = (checked: boolean): void => {
        const action = checked ? enable() : disable()
        action.catch(error => console.error(error))
    }

    const categoriesDisabled = bridgeAvailable && (permissionStatus !== 'granted' || ! globalEnabled)

    return (
        <div className="notification-settings">
            <h1 className="notification-settings__title">
                {trans('pages.notificationSettings.title')}
            </h1>

            <EnableNotificationsBanner
                context="notificationSettings"
                className="notification-settings__cta-banner"
            />

            <ul className="notification-settings__list">
                {bridgeAvailable && (
                    <InfoListItem
                        disabled={permissionStatus !== 'granted'}
                        label={(
                            <label htmlFor={GLOBAL_SWITCH_ID} className="notification-settings__label">
                                {trans('pages.notificationSettings.globalToggle')}
                            </label>
                        )}
                    >
                        <input
                            id={GLOBAL_SWITCH_ID}
                            type="checkbox"
                            {...SWITCH_ATTRIBUTE}
                            className="notification-settings__global-switch"
                            checked={globalEnabled}
                            disabled={globalLoading || permissionStatus !== 'granted'}
                            onChange={event => handleGlobalChange(event.target.checked)}
                        />
                    </InfoListItem>
                )}

                {CATEGORY_ORDER.map(category => (
                    <InfoListItem
                        key={category}
                        disabled={categoriesDisabled}
                        label={(
                            <label htmlFor={getCategorySelectId(category)} className="notification-settings__label">
                                {trans(`pages.notificationSettings.categories.${category}`)}
                            </label>
                        )}
                    >
                        {bridgeAvailable ? (
                            <Select
                                id={getCategorySelectId(category)}
                                label={trans(`pages.notificationSettings.categories.${category}`)}
                                value={levels[category]}
                                onChange={value => handleCategoryChange(category, value)}
                                options={getLevelOptions(category)}
                                disabled={categoriesDisabled}
                                className="notification-settings__level-select"
                            />
                        ) : (
                            <input
                                id={getCategorySelectId(category)}
                                type="checkbox"
                                checked={levels[category] !== NotificationLevel.off}
                                onChange={event => handleCategoryCheckboxChange(category, event.target.checked)}
                            />
                        )}
                    </InfoListItem>
                ))}
            </ul>

            {(globalLoading || ! hydrated) && <LoadingSpinner />}
        </div>
    )
}
